<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Customers List</h1>
        <span class="page-subtitle">Manage and view all registered users</span>
      </div>
      <div class="page-header__right">
        <button class="btn-export" @click="exportToCSV" v-if="items && items.length" title="Export to CSV">
          <i class="fa fa-download"></i> Export CSV
        </button>
        <button class="btn-primary-action" @click="showCreateModal()">
          <i class="fa fa-plus"></i> New User
        </button>
      </div>
    </div>

    <!-- ── Search ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">
        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search by email, name or phone number..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>
        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Created At</th>
            <th>Name</th>
            <th>Country</th>
            <th>Phone</th>
            <th>Email</th>
            <th class="sortable-th" @click="sortData('credit')">
              Credit
              <i :class="sortByCredit ? 'fa fa-angle-down' : 'fa fa-angle-up'" class="sort-icon"></i>
            </th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <router-link
              tag="tr"
              :to="{ name: 'user.show', params: { id: user.id } }"
              v-for="user in items"
              :key="user.id"
              class="dd-table__row"
          >
            <td><span class="order-id">#{{ user.id }}</span></td>
            <td class="date-cell">{{ formatDate(user.created_at) || '—' }}</td>
            <td>
              <div class="customer-info">
                <div class="customer-avatar">
                  {{ (user.first_name || user.email || '?')[0].toUpperCase() }}
                </div>
                <span class="customer-name">{{ user.first_name }} {{ user.last_name }}</span>
              </div>
            </td>
            <td>
              <span class="country-cell" v-if="user.address">{{ user.address.country.name }}</span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td class="text-muted">{{ user.phone_number || '—' }}</td>
            <td class="email-cell">{{ user.email }}</td>
            <td>
                <span class="credit-val" :class="user.balance > 0 ? 'credit-val--pos' : ''">
                  {{ user.balance }} AED
                </span>
            </td>
            <td @click.prevent>
              <b-dropdown variant="none" no-caret boundary="viewport" right>
                <template #button-content>
                  <div class="action-menu-btn">
                    <i class="fa fa-ellipsis-h"></i>
                  </div>
                </template>
                <b-dropdown-item @click.prevent="loginAsUser(user)">
                  <i class="fa fa-sign-in mr-2"></i> Login as User
                </b-dropdown-item>
                <b-dropdown-item @click.prevent="showAddOrDeductCreditModal(user)">
                  <i class="fa fa-credit-card mr-2"></i> Add / Deduct Credit
                </b-dropdown-item>
                <b-dropdown-item @click.prevent="showPurchasesList(user.id)">
                  <i class="fa fa-history mr-2"></i> Purchase History
                </b-dropdown-item>
                <b-dropdown-item @click.prevent="showWalletHistory(user.id)">
                  <i class="fa fa-wallet mr-2"></i> Credit History
                </b-dropdown-item>
              </b-dropdown>
            </td>
          </router-link>

          <tr v-if="items && items.length === 0">
            <td colspan="8" class="empty-state">
              <i class="fa fa-users"></i>
              <div>No users found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">
          Showing {{ items ? items.length : 0 }} of {{ totalItems }} users
        </div>
        <b-pagination
            v-model="page"
            :total-rows="totalItems"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

    <!-- ── Create User Modal ── -->
    <modal name="create-modal" height="auto" :scrollable="true">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Create New User</span>
          <button class="dd-modal__close" @click="$modal.hide('create-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">First Name</label>
              <input v-model="createForm.first_name" class="dd-input" placeholder="First name" />
            </div>
            <div class="form-field">
              <label class="filter-label">Last Name</label>
              <input v-model="createForm.last_name" class="dd-input" placeholder="Last name" />
            </div>
            <div class="form-field">
              <label class="filter-label">Email <span class="required">*</span></label>
              <input v-model="createForm.email" type="email" class="dd-input" placeholder="email@example.com" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Password <span class="required">*</span></label>
              <input v-model="createForm.password" type="password" class="dd-input" placeholder="••••••••" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Phone Number</label>
              <input v-model="createForm.phone_number" class="dd-input" placeholder="+971..." />
            </div>
            <div class="form-field">
              <label class="filter-label">Whatsapp Number</label>
              <input v-model="createForm.whatsapp_number" class="dd-input" placeholder="+971..." />
            </div>
            <div class="form-field">
              <label class="filter-label">Telegram Number</label>
              <input v-model="createForm.telegram_number" class="dd-input" placeholder="@username" />
            </div>
            <div class="form-field">
              <label class="filter-label">Gender</label>
              <select v-model="createForm.gender" class="dd-input">
                <option value="1">Man</option>
                <option value="2">Woman</option>
                <option value="3">Unknown</option>
              </select>
            </div>
            <div class="form-field form-field--full">
              <label class="filter-label">Birthday</label>
              <input v-model="createForm.birth_date" type="date" class="dd-input" />
            </div>
          </div>

          <div class="section-divider">
            <span>User Address</span>
          </div>

          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Country</label>
              <country-search-box @selected="(id) => createForm.country_id = id"></country-search-box>
            </div>
            <div class="form-field">
              <label class="filter-label">City</label>
              <city-search-box :country-id="createForm.country_id" @selected="(id) => createForm.city_id = id"></city-search-box>
            </div>
            <div class="form-field form-field--wide">
              <label class="filter-label">Address</label>
              <input v-model="createForm.address" class="dd-input" placeholder="Street address" />
            </div>
            <div class="form-field">
              <label class="filter-label">Postcode</label>
              <input v-model="createForm.post_code" class="dd-input" placeholder="Postcode" />
            </div>
          </div>

          <ul class="error-list" v-if="createForm.errors && createForm.errors.length">
            <li v-for="error in createForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('create-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="createUser">
            <i class="fa fa-user-plus"></i> Create User
          </button>
        </div>
      </div>
    </modal>

    <!-- ── Credit Modal ── -->
    <modal name="change-credit-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Customer Credits Management</span>
          <button class="dd-modal__close" @click="$modal.hide('change-credit-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="credit-balance">
            <div class="credit-balance__label">Available Credit</div>
            <div class="credit-balance__val">{{ changeCreditForm.currentBalance }} AED</div>
          </div>

          <b-tabs class="dd-tabs" nav-class="dd-tabs__nav">
            <b-tab title="Add Credit" active>
              <div class="form-field mt-3">
                <label class="filter-label">Amount to Add</label>
                <input v-model="changeCreditForm.amount" class="dd-input" type="number" placeholder="0.00" />
              </div>
              <div class="form-field mt-3">
                <label class="filter-label">Admin Password</label>
                <input v-model="changeCreditForm.password" class="dd-input" type="password" placeholder="••••••••" />
              </div>
              <button class="btn-submit btn-submit--green mt-3 w-100" @click.prevent="changeCredit('deposit')">
                <i class="fa fa-plus"></i> Add Credit
              </button>
            </b-tab>
            <b-tab title="Deduct Credit">
              <div class="form-field mt-3">
                <label class="filter-label">Amount to Deduct</label>
                <input v-model="changeCreditForm.amount" class="dd-input" type="number" placeholder="0.00" />
              </div>
              <div class="form-field mt-3">
                <label class="filter-label">Admin Password</label>
                <input v-model="changeCreditForm.password" class="dd-input" type="password" placeholder="••••••••" />
              </div>
              <button class="btn-submit btn-submit--red mt-3 w-100" @click.prevent="changeCredit('withdraw')">
                <i class="fa fa-minus"></i> Deduct Credit
              </button>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config } from "../../helpers";
import SearchBox from "../../components/SearchBox.vue";
import CountrySearchBox from "../../components/CountrySearchBox.vue";
import CitySearchBox from "../../components/CitySearchBox.vue";
import { router } from "@/router";

export default {
  name: "Users",
  components: {
    "search-box": SearchBox,
    "country-search-box": CountrySearchBox,
    "city-search-box": CitySearchBox,
  },
  data() {
    return {
      url: "",
      items: [],
      inputFilters: {
        q: null,
        sort: { credit: null },
      },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      totalItems: 1,
      perPage: 1,
      createForm: {
        country_id: null,
        city_id: null,
      },
      changeCreditForm: {
        id: 0,
        currentBalance: 0.0,
        amount: 0.0,
        password: "",
      },
      sortByCredit: false,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/users";
    if (this.$route.params?.q) {
      this.inputFilters.q = this.$route.params.q;
      this.filter();
    }
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page };
      if (this.isFiltered) {
        myData = { ...myData, q: this.filters.q, sort: { balance: this.filters.sort_credit } };
      }
      axios.get(this.url, { params: myData }).then((r) => {
        this.items      = r.data.data;
        this.page       = r.data.meta.current_page;
        this.lastPage   = r.data.meta.last_page;
        this.totalItems = r.data.meta.total;
        this.perPage    = r.data.meta.per_page;
      });
    },

    filter() {
      this.page = 1;
      this.isFiltered = true;
      const filters = {};
      if (this.inputFilters.q) filters.q = this.inputFilters.q;
      if (this.inputFilters.sort.credit) filters.sort_credit = this.inputFilters.sort.credit;
      this.filters = filters;
      this.loadData();
    },

    sortData() {
      this.sortByCredit = !this.sortByCredit;
      this.inputFilters.sort.credit = this.sortByCredit ? "DESC" : "ASC";
      this.filter();
    },

    createUser() {
      axios
          .post(this.url, this.createForm)
          .then(() => {
            this.createForm = { country_id: null, city_id: null };
            alert("User created successfully!");
            this.$modal.hide("create-modal");
            this.loadData();
          })
          .catch((e) => {
            alert(Object.values(e.response.data.errors).flat()[0] ?? e.response.data?.meta?.message);
          });
    },

    exportToCSV() {
      const myData = this.isFiltered ? { q: this.filters.q } : {};
      axios.get(this.url + "/export", { params: myData }).then((r) => {
        const url  = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "users.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    },

    changeCredit(action) {
      axios
          .post(`${this.url}/${this.changeCreditForm.id}/wallet/${action}`, {
            amount:   this.changeCreditForm.amount,
            password: this.changeCreditForm.password,
          })
          .then(() => this.showWalletHistory(this.changeCreditForm.id))
          .catch((e) => {
            alert(Object.values(e.response?.data?.errors ?? ["Something went wrong!"]).flat()[0]);
          });
    },

    showPurchasesList(userId)  { this.$router.push(`/users/${userId}/purchases`); },
    showWalletHistory(userId)  { this.$router.push(`/users/${userId}/wallet-history`); },
    showCreateModal()          { this.$modal.show("create-modal"); },

    loginAsUser(user) {
      axios.post(`${config("BACKEND_ROOT")}/admin/users/${user.id}/login`).then((r) => {
        const { access_token: token, refresh_token: refresh, cart } = r.data;
        window.open(
            `${config("FRONTEND_ROOT")}/auth/autologin?token=${token}&refresh=${refresh}&uuid=${cart.uuid}&count=${cart.items.length}&to=/dashboard`,
            "_blank"
        ).focus();
      });
    },

    showAddOrDeductCreditModal(user) {
      this.changeCreditForm = { id: user.id, currentBalance: user.balance, amount: 0.0, password: "" };
      this.$modal.show("change-credit-modal");
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },
  },

  watch: {
    page() { this.loadData(); },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap    { display: flex; flex-direction: column; gap: 16px; }
.page-header  { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; }

.btn-export { height: 36px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-export:hover { border-color: var(--accent); color: var(--accent); }

.btn-primary-action { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-primary-action:hover { opacity: .88; }

/* ── Filter ── */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }

.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }

/* ── Table ── */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); white-space: nowrap; text-align: left; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row { cursor: pointer; transition: background .12s; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.sortable-th { cursor: pointer; user-select: none; }
.sortable-th:hover { color: var(--text-1); }
.sort-icon { margin-left: 4px; font-size: 12px; }

.order-id { font-weight: 500; color: var(--text-1); }
.date-cell { font-size: 12px; color: var(--text-3); white-space: nowrap; }
.email-cell { font-size: 12px; color: var(--text-2); }
.text-muted { color: var(--text-3); font-size: 12px; }
.country-cell { font-size: 13px; color: var(--text-2); }

.customer-info   { display: flex; align-items: center; gap: 9px; }
.customer-avatar { width: 30px; height: 30px; background: rgba(244,47,75,.12); color: var(--accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.customer-name   { font-size: 13px; font-weight: 500; color: var(--text-1); }

.credit-val       { font-weight: 500; color: var(--text-2); }
.credit-val--pos  { color: #22c55e; }

/* Action menu */
.action-menu-btn {
  width: 30px; height: 30px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-2); font-size: 14px;
  transition: border-color .15s, color .15s;
}
.action-menu-btn:hover { border-color: var(--accent); color: var(--accent); }

/* Fix b-dropdown button */
.dd-table__row td:last-child .btn { padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }

/* Empty */
.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.dd-pagination >>> .page-link { background: var(--bg-hover); border-color: var(--border); color: var(--text-2); font-size: 13px; }
.dd-pagination >>> .page-item.active .page-link { background: var(--accent); border-color: var(--accent); color: #fff; }
.dd-pagination >>> .page-link:hover { background: var(--bg-hover); border-color: var(--accent); color: var(--accent); }

/* ══════════════════════════════════════
   Modals
══════════════════════════════════════ */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }

/* Form grid */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-field { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: span 2; }
.form-field--wide { grid-column: span 1; }
.required { color: var(--accent); }

.section-divider { display: flex; align-items: center; gap: 12px; margin: 1.25rem 0; color: var(--text-3); font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; }
.section-divider::before, .section-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }

/* Credit modal */
.credit-balance { background: var(--bg-hover); border: 1px solid var(--border); border-radius: 10px; padding: 12px 16px; margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; }
.credit-balance__label { font-size: 12px; color: var(--text-3); }
.credit-balance__val   { font-size: 18px; font-weight: 600; color: #22c55e; }

/* Tabs override */
.dd-tabs >>> .nav-pills .nav-link { background: var(--bg-hover); border: 1px solid var(--border); color: var(--text-2); font-size: 13px; border-radius: 8px; margin-right: 6px; }
.dd-tabs >>> .nav-pills .nav-link.active { background: var(--accent); border-color: var(--accent); color: #fff; }

.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; width: auto; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }
.btn-submit--green { background: #16a34a; }
.btn-submit--red   { background: #f42f4b; }
.w-100 { width: 100% !important; }
.mt-3  { margin-top: 1rem; }

.error-list { margin-top: 10px; padding-left: 16px; color: var(--accent); font-size: 13px; }

@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field, .filter-field--grow { width: 100%; }
  .form-grid { grid-template-columns: 1fr; }
  .form-field--full { grid-column: span 1; }
}
</style>